import React, { useCallback, useEffect, useState } from "react";
import { Skeleton } from "antd";
import moment from "moment";
import Button from "@dtci/spellbook/Button";
import { usePPTState, usePPTActions } from "tap/store/ppt/usePPT";
import { usePPTToasts } from "../../hooks/usePPTToasts";
import { fetchDeliveryNotes, createDeliveryNote } from "ppt/api";
import styles from "./styles.module.scss";

/**
 * Component for displaying and creating notes for a delivery.
 * Fetches notes when mounted and provides textarea + button for creating new notes.
 */
export const DeliveryNotesList = () => {
  const { selectedDelivery, promotionDetail, deliveryNotes, deliveryNotesLoading, deliveryDrawerOpen } =
    usePPTState();
  const { updateDeliveryNotes, setDeliveryNotesLoading } = usePPTActions();
  const { showSuccessToast, showErrorToast } = usePPTToasts();

  const [noteText, setNoteText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const promotionId = promotionDetail?.id;
  const clientKey = selectedDelivery?.clientKey;

  // Fetch notes when drawer opens or delivery changes
  useEffect(() => {
    if (!promotionId || !selectedDelivery || !deliveryDrawerOpen) return;

    let cancelled = false;
    setDeliveryNotesLoading(true);

    fetchDeliveryNotes(promotionId, clientKey)
      .then((data) => {
        if (!cancelled) {
          updateDeliveryNotes(data.notes || []);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch delivery notes", err);
        showErrorToast("Failed to load notes. Please try again.");
        updateDeliveryNotes([]);
      })
      .finally(() => {
        if (!cancelled) setDeliveryNotesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [promotionId, clientKey, deliveryDrawerOpen, setDeliveryNotesLoading, updateDeliveryNotes, showErrorToast]);

  const handleSubmit = useCallback(async () => {
    if (!noteText.trim()) return;
    if (!promotionId || !selectedDelivery) return;

    setSubmitting(true);
    try {
      await createDeliveryNote(promotionId, clientKey, noteText.trim());
      showSuccessToast("Note added successfully");
      setNoteText("");

      // Refetch notes to show the new one
      const data = await fetchDeliveryNotes(promotionId, clientKey);
      updateDeliveryNotes(data.notes || []);
    } catch (err) {
      console.error("Failed to create delivery note", err);
      showErrorToast("Failed to add note. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [
    noteText,
    promotionId,
    clientKey,
    selectedDelivery,
    showSuccessToast,
    showErrorToast,
    updateDeliveryNotes,
  ]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  if (deliveryNotesLoading) {
    return <Skeleton active paragraph={{ rows: 4 }} />;
  }

  return (
    <>
      {deliveryNotes && deliveryNotes.length > 0 && (
        <ul className={styles.notesList}>
          {deliveryNotes.map((note) => (
            <li key={note.id} className={styles.noteItem}>
              <div className={styles.noteHeader}>
                <span className={styles.noteAuthor}>{note.authorName}</span>
                <span className={styles.noteEmail}>({note.authorEmail})</span>
                <span className={styles.noteTimestamp}>
                  {moment(note.createdAt).format("MMM D, YYYY h:mm A")}
                </span>
              </div>
              <div className={styles.noteText}>{note.noteText}</div>
            </li>
          ))}
        </ul>
      )}

      <div className={styles.noteInputSection}>
        <textarea
          className={styles.noteTextarea}
          placeholder="Add a note about this delivery..."
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={4}
          disabled={submitting}
        />
        <div className={styles.noteActions}>
          <Button
            onClick={handleSubmit}
            disabled={!noteText.trim() || submitting}
            isFetching={submitting}
            styleType="primary"
          >
            Add Note
          </Button>
        </div>
      </div>
    </>
  );
};
